package dev.lingvo.shared.screenshots.doc

import dev.lingvo.shared.StoreLimits
import dev.lingvo.shared.screenshots.render.upgradeLocaleOverrides
import kotlinx.serialization.SerializationException
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.intOrNull

sealed interface DecodeResult {
    /** [canonical] is [DesignCodec.encode] of [design]: the bytes the server stores and hashes. */
    data class Ok(val design: ScreenshotDesign, val warnings: List<DesignIssue>, val canonical: String) : DecodeResult

    /** 1..[DesignCodec.MAX_ISSUES] issues, errors first. */
    data class Refused(val issues: List<DesignIssue>) : DecodeResult
}

/**
 * Migration steps keyed by the version they upgrade *from*: `steps[v]` turns a v document into a v + 1 one.
 * The keys are exactly `1 until DESIGN_SCHEMA_VERSION`, so the map is empty at v1. A missing step refuses the
 * document; a version is never guessed. Because the canonical form omits defaults, changing a default is a
 * schema bump whose step writes the old default into documents that omitted it.
 */
object DesignMigrations {
    val steps: Map<Int, (JsonObject) -> JsonObject> = mapOf(
        1 to { old -> JsonObject(old + ("schemaVersion" to JsonPrimitive(2))) },
        2 to { old ->
            val design = DesignCodec.json.decodeFromJsonElement(ScreenshotDesign.serializer(), old)
            DesignCodec.json.encodeToJsonElement(ScreenshotDesign.serializer(), upgradeLocaleOverrides(design)) as JsonObject
        },
    )
}

/**
 * The only way a design enters or leaves Lingvo (SPEC-B 1.3).
 *
 * [decode] never throws: every failure, including every parser exception, becomes a [DesignIssue]. The server
 * stores [DecodeResult.Ok.canonical], never the raw body, so duplicate keys, whitespace, defaults written out and
 * number spellings vanish and the stored bytes are the same whichever platform encoded them.
 */
object DesignCodec {
    const val MAX_ISSUES = 50

    val json: Json = Json {
        ignoreUnknownKeys = false
        isLenient = false
        allowSpecialFloatingPointValues = false
        classDiscriminator = "type"
        // Defaults are omitted from the stored form (about half the bytes) and filled in by the decoder.
        encodeDefaults = false
        explicitNulls = true
    }

    fun decode(raw: String, devices: DeviceClassRule, fonts: FontNameRule = DesignFonts.V1): DecodeResult {
        JsonPrecheck.scan(raw)?.let { return DecodeResult.Refused(listOf(it)) }
        val element = try {
            Json.parseToJsonElement(raw)
        } catch (e: Exception) {
            return refused(DesignIssueCode.MALFORMED, "", "The design is not valid JSON.")
        }
        return decodeElement(element, devices, fonts)
    }

    /**
     * Decodes an already-parsed document, e.g. the `document` of a request envelope. The element is re-checked
     * against the same depth, size and finiteness limits, iteratively, before any recursive decoder touches it.
     */
    fun decodeElement(element: JsonElement, devices: DeviceClassRule, fonts: FontNameRule = DesignFonts.V1): DecodeResult =
        // Supported documents retain their schema, semantics and bytes. Feature edits explicitly opt into V3.
        decodeElement(element, devices, fonts, DesignMigrations.steps,
            ((element as? JsonObject)?.get("schemaVersion") as? JsonPrimitive)?.intOrNull
                ?.takeIf { it in 1..DESIGN_SCHEMA_VERSION } ?: DESIGN_SCHEMA_VERSION)

    /** The same with an explicit migration registry and current version, so tests can exercise the migration loop. */
    internal fun decodeElement(
        element: JsonElement,
        devices: DeviceClassRule,
        fonts: FontNameRule,
        steps: Map<Int, (JsonObject) -> JsonObject>,
        currentVersion: Int,
    ): DecodeResult = try {
        decodeChecked(element, devices, fonts, steps, currentVersion)
    } catch (e: Exception) {
        // A safety net: every expected failure is mapped below, so reaching this is a bug, never a crash. Errors
        // (StackOverflowError, a browser RangeError) are deliberately not caught: the limits make them impossible.
        refused(DesignIssueCode.MALFORMED, "", "The design could not be read.")
    }

    private fun decodeChecked(
        element: JsonElement,
        devices: DeviceClassRule,
        fonts: FontNameRule,
        steps: Map<Int, (JsonObject) -> JsonObject>,
        currentVersion: Int,
    ): DecodeResult {
        val root = element as? JsonObject
            ?: return refused(DesignIssueCode.MALFORMED, "", "A design must be a JSON object.")
        JsonElementScan.scan(root)?.let { return DecodeResult.Refused(listOf(it)) }
        val found = when (val version = readVersion(root, steps, currentVersion)) {
            is VersionRead.Found -> version.version
            is VersionRead.Problem -> return DecodeResult.Refused(listOf(version.issue))
        }
        var current = root
        for (from in found until currentVersion) {
            val step = steps[from] ?: return refused(
                DesignIssueCode.UNSUPPORTED_VERSION,
                "/schemaVersion",
                "No migration from design version $from; this Lingvo reads up to $currentVersion.",
            )
            current = try {
                step(current)
            } catch (e: Exception) {
                return refused(DesignIssueCode.MALFORMED, "", "The design could not be upgraded from version $from.")
            }
        }
        val design = try {
            // Decoded from text, not from the tree: kotlinx's tree decoder reports no JSON path, the streaming one
            // does. The text is the normalized form of an element already held to the depth and size limits.
            json.decodeFromString(ScreenshotDesign.serializer(), normalizedJson(current))
        } catch (e: SerializationException) {
            return DecodeResult.Refused(listOf(DecodeErrors.map(e)))
        } catch (e: Exception) {
            return refused(DesignIssueCode.MALFORMED, "", "The design does not match the version 1 format.")
        }
        StrictTypes.check(current, ScreenshotDesign.serializer().descriptor)?.let { return DecodeResult.Refused(listOf(it)) }
        val issues = DesignValidator.validate(design, devices, fonts)
        val errors = issues.filter { it.severity == Severity.ERROR }
        if (errors.isNotEmpty()) {
            val warnings = issues.filter { it.severity == Severity.WARNING }
            return DecodeResult.Refused((errors + warnings).take(MAX_ISSUES))
        }
        val canonical = encode(design)
        val bytes = StoreLimits.utf8Length(canonical)
        if (bytes > DesignLimits.MAX_CANONICAL_BYTES) {
            return refused(
                DesignIssueCode.PAYLOAD_TOO_LARGE,
                "",
                "The design is $bytes bytes when stored; the limit is ${DesignLimits.MAX_CANONICAL_BYTES}.",
            )
        }
        return DecodeResult.Ok(design, issues, canonical)
    }

    /**
     * The canonical form: defaults omitted, keys in declaration order (so `schemaVersion` is first), override and
     * patch keys sorted, numbers spelled by [normalizedNumber]. Call it on a design that passed [DesignValidator];
     * a non-finite number throws [SerializationException] because JSON cannot carry it.
     */
    fun encode(design: ScreenshotDesign): String =
        normalizedJson(json.encodeToJsonElement(ScreenshotDesign.serializer(), sortedMaps(design)))

    /** The same design with its override and patch maps in key order, so equal designs encode equally. */
    fun sortedMaps(design: ScreenshotDesign): ScreenshotDesign = design.copy(
        boards = design.boards.map { board ->
            board.copy(
                pages = board.pages.map { page ->
                    page.copy(
                        overrides = page.overrides.entries.sortedBy { it.key }.associate { (locale, override) ->
                            locale to override.copy(
                                patches = override.patches.entries.sortedBy { it.key }.associate { it.key to it.value },
                            )
                        },
                    )
                },
            )
        },
    )

    private fun refused(code: DesignIssueCode, path: String, message: String): DecodeResult.Refused =
        DecodeResult.Refused(listOf(DesignIssue(code, path, message)))

    private sealed interface VersionRead {
        data class Found(val version: Int) : VersionRead
        data class Problem(val issue: DesignIssue) : VersionRead
    }

    private fun readVersion(root: JsonObject, steps: Map<Int, (JsonObject) -> JsonObject>, currentVersion: Int): VersionRead {
        val path = "/schemaVersion"
        val value = root["schemaVersion"] ?: return VersionRead.Problem(
            DesignIssue(DesignIssueCode.MISSING_VERSION, path, "The design has no schemaVersion."),
        )
        val primitive = value as? JsonPrimitive
        if (primitive == null || primitive.isString || !INTEGER_REGEX.matches(primitive.content)) {
            return VersionRead.Problem(
                DesignIssue(DesignIssueCode.MISSING_VERSION, path, "schemaVersion must be a whole number."),
            )
        }
        val version = primitive.content.toIntOrNull()
        return when {
            version == null || version > currentVersion -> VersionRead.Problem(
                DesignIssue(
                    DesignIssueCode.UNSUPPORTED_VERSION,
                    path,
                    "This design was saved by a newer Lingvo (version ${primitive.content.take(12)}); " +
                        "this one reads up to $currentVersion.",
                ),
            )
            // Versions start at 1; an older one is read only when a migration step starts from it.
            version < 1 && version !in steps -> VersionRead.Problem(
                DesignIssue(
                    DesignIssueCode.UNSUPPORTED_VERSION,
                    path,
                    "Design version $version does not exist; versions start at 1 and this Lingvo reads up to " +
                        "$currentVersion.",
                ),
            )
            else -> VersionRead.Found(version)
        }
    }

    private val INTEGER_REGEX = Regex("^-?[0-9]+$")
}
